import { ref, computed, onMounted, onBeforeUnmount, watch, type MaybeRefOrGetter, toValue } from 'vue';
import { ibWS } from '@ibgroup-tws-webapps/ib.utils';
import { useUserState } from '@/stores/useUserState';

export interface OpenInterestStreamOptions {
  isLive?: MaybeRefOrGetter<boolean>;
}

export function useOpenInterestStream(
  conid: MaybeRefOrGetter<number | undefined>,
  exchange: MaybeRefOrGetter<string>,
  options: OpenInterestStreamOptions = {},
) {
  const putInterest = ref<number | null>(null);
  const callInterest = ref<number | null>(null);
  const userState = useUserState();

  const shouldStream = computed(() => !userState.usePublicApi);

  let requestId: string | null = null;
  let subscription: { unsubscribe(): void } | null = null;
  let generation = 0;
  const mounted = ref(false);

  async function subscribe() {
    const currentGeneration = ++generation;
    const c = toValue(conid);
    const ex = toValue(exchange);
    if (!c || !ex) return;

    const webAppId = await ibWS.getWebAppId();
    if (currentGeneration !== generation) return;
    requestId = `ec-oi-${c}-${ex}-${webAppId}`;

    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: {
          destination: '/event-contract/open-interest',
          'x-target': 'mdfarm',
          id: requestId,
        },
        body: { conid: c, exchange: ex },
      },
    });

    const observable = ibWS.receive(requestId);
    if (observable) {
      subscription = observable.subscribe({
        /* eslint-disable @typescript-eslint/no-explicit-any */
        next: (msg: any) => {
          if (currentGeneration !== generation) return;
          const body = msg?.payload ?? msg?.body ?? msg;
          if (body && typeof body.putInterest !== 'undefined') {
            const val = Number(body.putInterest);
            if (!isNaN(val)) putInterest.value = val;
          }
          if (body && typeof body.callInterest !== 'undefined') {
            const val = Number(body.callInterest);
            if (!isNaN(val)) callInterest.value = val;
          }
        },
      });
    }
  }

  function unsubscribe() {
    generation++;
    putInterest.value = null;
    callInterest.value = null;
    if (requestId) {
      ibWS.sendMessage({
        message: {
          command: 'SEND',
          headers: {
            destination: '/md/stream/unsubscribe',
            'x-target': 'mdfarm',
            id: requestId,
          },
        },
      });
    }
    subscription?.unsubscribe();
    subscription = null;
    requestId = null;
  }

  onMounted(() => {
    mounted.value = true;
  });

  onBeforeUnmount(unsubscribe);

  watch(
    [
      mounted,
      shouldStream,
      () => toValue(conid),
      () => toValue(exchange),
      () => options.isLive === undefined || toValue(options.isLive),
    ],
    () => {
      unsubscribe();
      if (
        mounted.value &&
        shouldStream.value &&
        toValue(conid) &&
        toValue(exchange) &&
        (options.isLive === undefined || toValue(options.isLive))
      ) {
        void subscribe();
      }
    },
  );

  return { putInterest, callInterest };
}
